import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import { sendInvitation } from 'apis/UserAPI';
import DownCaretIcon from 'common/Icons/DownCaretIcon';
import ConfirmModal from 'common/Modal/ConfirmModal';
import { getUserSwitchRedirectPath, queueSupportModeRedirectPath, useUserSwitch } from 'components/layout/hooks';
import { useRootContext } from 'components/layout/RootContext';
import Button from 'components/lib/Button';
import Dropdown from 'components/lib/Dropdown';
import { useAppSelector } from 'store/hook';

import { STATUS } from '../../../constants';
import { UserManagementUserType } from '../types';
import { useUserManagementContext } from '../UserManagementContext';
import UserManagementFeatureFlagsModal from '../UserManagementFeatureFlagsModal';
import UserManagementUserModal from '../UserManagementUserModal';

type Props = {
  item: UserManagementUserType;
};

const Wrapper = styled.div`
  visibility: hidden;
  display: flex;
  justify-content: flex-end;
`;

const ActionButtonContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const InvitationDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SecondaryNote = styled.div`
  color: var(--text-secondary);
`;

const UserManagementTableActions = ({ item }: Props) => {
  const navigate = useNavigate();
  const { showMessage } = useRootContext();
  const { currentUser } = useAppSelector((state) => state.auth);
  const { refetch } = useUserManagementContext();
  const { handleUserSwitch } = useUserSwitch();

  const [status, setStatus] = useState(STATUS.IDLE);
  const [sendInviteModalOpen, setSendInviteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [featureFlagsModalOpen, setFeatureFlagsModalOpen] = useState(false);

  const handleSendInvite = async () => {
    try {
      setStatus(STATUS.LOADING);
      await sendInvitation(item.id);
      showMessage('success', 'User invitation sent!');
      refetch();
      setStatus(STATUS.LOADED);
    } catch (error) {
      setStatus(STATUS.FAILURE);
      showMessage('error', 'Trouble sending user invitation.');
      console.error('Trouble sending user invitation.', error);
    } finally {
      setSendInviteModalOpen(false);
    }
  };

  const onEnterSupportMode = () => {
    handleUserSwitch(item.auth_user_id, (userInfo) => {
      const redirectPath = getUserSwitchRedirectPath(userInfo);
      queueSupportModeRedirectPath(redirectPath);
      navigate(redirectPath);
    });
  };

  const supportModeEnabled = Boolean(
    currentUser?.isSuperuser && !item.is_superuser && item.is_active && currentUser?.user_id !== item.auth_user_id
  );
  const canManageFeatureFlags = Boolean(currentUser?.isSuperuser || currentUser?.isStaff);

  const menuItems = [
    {
      label: <span data-test-id={`user-management-user-${item.id}-action-edit`}>Edit</span>,
      key: 'edit',
      onClick: () => setEditModalOpen(true),
    },
    ...(canManageFeatureFlags
      ? [
          {
            label: <span data-test-id={`user-management-user-${item.id}-action-feature-flags`}>Set Feature Flags</span>,
            key: 'set-feature-flags',
            onClick: () => setFeatureFlagsModalOpen(true),
          },
        ]
      : []),
    {
      label: <span data-test-id={`user-management-user-${item.id}-action-send-invitation`}>Send Invitation</span>,
      key: 'send-invitation',
      onClick: () => setSendInviteModalOpen(true),
    },
    ...(supportModeEnabled
      ? [
          {
            label: (
              <span data-test-id={`user-management-user-${item.id}-action-enter-support-mode`}>Enter Support Mode</span>
            ),
            key: 'enter-support-mode',
            onClick: onEnterSupportMode,
          },
        ]
      : []),
  ];

  return (
    <Wrapper className="hover-visible">
      {editModalOpen && (
        <UserManagementUserModal open={editModalOpen} onClose={() => setEditModalOpen(false)} mode="edit" user={item} />
      )}
      {featureFlagsModalOpen && (
        <UserManagementFeatureFlagsModal
          open={featureFlagsModalOpen}
          onClose={() => setFeatureFlagsModalOpen(false)}
          user={item}
        />
      )}
      <ConfirmModal
        open={sendInviteModalOpen}
        title={`Are you sure you want to send invitation to ${item.name || item.email || 'this user'}?`}
        description={
          <InvitationDescription>
            <div>
              By proceeding, an email will be sent to {item.email} containing login credentials, along with instructions
              for setting a new password upon first successful login.
            </div>
            <SecondaryNote>
              <b>Please note:</b> The current user's login credentials will become invalid once this action is
              completed.
            </SecondaryNote>
          </InvitationDescription>
        }
        loading={status === STATUS.LOADING}
        confirmLabel={'Confirm'}
        confirmButtonDataTestId={`user-management-user-${item.id}-send-invitation-confirm`}
        cancelButtonDataTestId={`user-management-user-${item.id}-send-invitation-cancel`}
        onConfirm={handleSendInvite}
        onCancel={() => setSendInviteModalOpen(false)}
      />
      <Dropdown
        menu={{
          items: menuItems,
        }}
      >
        <Button data-test-id={`user-management-user-${item.id}-actions-button`}>
          <ActionButtonContent>
            <span>Actions</span>
            <DownCaretIcon />
          </ActionButtonContent>
        </Button>
      </Dropdown>
    </Wrapper>
  );
};

export default UserManagementTableActions;
